import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";

import { SiteHeader } from "@/components/site/SiteHeader";
import { Button } from "@/components/ui/button";
import { clearOpsLogFn, listOpsLog } from "@/lib/ops-log.functions";

export const Route = createFileRoute("/_authenticated/admin/ops-logs")({
  head: () => ({
    meta: [
      { title: "Admin · Ops logs — Mostrando México" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: OpsLogsPage,
});

type Category = "auth" | "bokun" | "sync" | "system";
type Severity = "info" | "warn" | "error";

function OpsLogsPage() {
  const list = useServerFn(listOpsLog);
  const clear = useServerFn(clearOpsLogFn);
  const qc = useQueryClient();
  const [category, setCategory] = useState<Category | "">("");
  const [severity, setSeverity] = useState<Severity | "">("");

  const query = useQuery({
    queryKey: ["ops-log", category, severity],
    queryFn: () =>
      list({
        data: {
          category: category || undefined,
          severity: severity || undefined,
          limit: 300,
        },
      }),
    refetchInterval: 5000,
  });

  const clearMut = useMutation({
    mutationFn: () => clear({}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ops-log"] }),
  });

  const entries = query.data?.entries ?? [];
  const counters = query.data?.counters;

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-6xl px-4 py-10 space-y-6">
        <header className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-3xl font-semibold">Registro operativo</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Fallos de autenticación, límites de Bokun y problemas de sincronización.
              Se actualiza cada 5 s.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => clearMut.mutate()}
            disabled={clearMut.isPending}
          >
            {clearMut.isPending ? "Limpiando…" : "Vaciar registro"}
          </Button>
        </header>

        {counters && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {(["auth", "bokun", "sync", "system"] as Category[]).map((c) => {
              const t = counters[c];
              return (
                <div key={c} className="rounded-xl border border-border p-3">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">{c}</div>
                  <div className="mt-1 flex gap-3 text-sm">
                    <span className="text-red-600 dark:text-red-400">✕ {t.error}</span>
                    <span className="text-amber-600 dark:text-amber-400">⚠ {t.warn}</span>
                    <span className="text-muted-foreground">ℹ {t.info}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <select
            className="h-9 rounded-lg border border-border bg-background px-3 text-sm"
            value={category}
            onChange={(e) => setCategory(e.target.value as Category | "")}
          >
            <option value="">Todas las categorías</option>
            <option value="auth">auth</option>
            <option value="bokun">bokun</option>
            <option value="sync">sync</option>
            <option value="system">system</option>
          </select>
          <select
            className="h-9 rounded-lg border border-border bg-background px-3 text-sm"
            value={severity}
            onChange={(e) => setSeverity(e.target.value as Severity | "")}
          >
            <option value="">Todas las severidades</option>
            <option value="error">error</option>
            <option value="warn">warn</option>
            <option value="info">info</option>
          </select>
          <Button variant="ghost" size="sm" onClick={() => query.refetch()}>
            Refrescar ahora
          </Button>
        </div>

        {query.isLoading ? (
          <p className="text-sm text-muted-foreground">Cargando…</p>
        ) : entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No hay eventos registrados para el filtro actual.
          </p>
        ) : (
          <div className="overflow-hidden rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">Fecha</th>
                  <th className="px-3 py-2">Sev</th>
                  <th className="px-3 py-2">Categoría</th>
                  <th className="px-3 py-2">Código</th>
                  <th className="px-3 py-2">Mensaje</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e, i) => (
                  <tr key={i} className="border-t border-border align-top">
                    <td className="px-3 py-2 whitespace-nowrap font-mono text-xs text-muted-foreground">
                      {new Date(e.ts).toLocaleString()}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={
                          e.severity === "error"
                            ? "text-red-600 dark:text-red-400"
                            : e.severity === "warn"
                              ? "text-amber-600 dark:text-amber-400"
                              : "text-muted-foreground"
                        }
                      >
                        {e.severity}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs">{e.category}</td>
                    <td className="px-3 py-2 font-mono text-xs">{e.code}</td>
                    <td className="px-3 py-2">
                      <div>{e.message}</div>
                      {e.context ? (
                        <pre className="mt-1 max-w-3xl overflow-x-auto rounded bg-muted p-2 text-xs text-muted-foreground">
                          {JSON.stringify(e.context, null, 2)}
                        </pre>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
